import { useState, useCallback } from 'react';
import type { 
  PlatformUser, 
  NewUserFormData, 
  UserUpdateData, 
  UserFilters, 
  UserBulkAction,
  UserAuditLog 
} from '../types/user';
import { mockUsers } from '../data/mockUsers';

export const useUserManagement = () => {
  const [users, setUsers] = useState<PlatformUser[]>(mockUsers);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [filters, setFilters] = useState<UserFilters>({});
  const [auditLogs, setAuditLogs] = useState<UserAuditLog[]>([]);
  const [loading, setLoading] = useState(false);

  const addAuditLog = useCallback((userId: string, action: string, details: string, performedBy: string) => {
    const log: UserAuditLog = {
      id: `log-${Date.now()}`,
      userId,
      action,
      details,
      timestamp: new Date().toISOString(),
      performedBy,
      ipAddress: '127.0.0.1' // In real implementation, this would come from the request
    };
    setAuditLogs(prev => [log, ...prev]);
  }, []);

  const filterUsers = useCallback((users: PlatformUser[], filters: UserFilters) => {
    return users.filter(user => {
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = 
          user.name.toLowerCase().includes(searchTerm) ||
          user.email.toLowerCase().includes(searchTerm);
        if (!matchesSearch) return false;
      }

      if (filters.roles?.length && !filters.roles.includes(user.role)) {
        return false;
      }

      if (filters.status?.length && !filters.status.includes(user.status)) {
        return false;
      }

      if (filters.groups?.length && !filters.groups.some(g => user.groups.includes(g))) {
        return false;
      }

      if (filters.department && user.profile.department?.id !== filters.department) {
        return false;
      }

      if (filters.company && user.profile.company?.id !== filters.company) {
        return false;
      }

      return true;
    });
  }, []);

  const getFilteredUsers = useCallback(() => {
    return filterUsers(users, filters);
  }, [users, filters, filterUsers]);

  const addUser = useCallback(async (newUser: NewUserFormData, currentUser: PlatformUser) => {
    setLoading(true);
    try {
      const userToAdd: PlatformUser = {
        ...newUser,
        id: `user-${Date.now()}`,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        requirePasswordChange: true,
        twoFactorEnabled: false,
        profile: {
          ...newUser.profile,
          lastPasswordChange: new Date().toISOString(),
          failedLoginAttempts: 0
        }
      };
      
      setUsers(prev => [...prev, userToAdd]);
      addAuditLog(userToAdd.id, 'CREATE', 'User created', currentUser.id);
      
      // In real implementation, this would be an API call
      // await api.users.create(userToAdd);
      
      return userToAdd;
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [addAuditLog]);

  const updateUser = useCallback(async (userData: UserUpdateData, currentUser: PlatformUser) => {
    setLoading(true);
    try {
      setUsers(prev => prev.map(user => {
        if (user.id !== userData.id) return user;
        
        const updatedUser = {
          ...user,
          ...userData,
          profile: {
            ...user.profile,
            ...userData.profile
          }
        };
        
        addAuditLog(user.id, 'UPDATE', 'User updated', currentUser.id);
        return updatedUser;
      }));
      
      // In real implementation, this would be an API call
      // await api.users.update(userData.id, userData);
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [addAuditLog]);

  const deleteUser = useCallback(async (userId: string, currentUser: PlatformUser) => {
    setLoading(true);
    try {
      setUsers(prev => prev.filter(user => user.id !== userId));
      addAuditLog(userId, 'DELETE', 'User deleted', currentUser.id);
      
      // In real implementation, this would be an API call
      // await api.users.delete(userId);
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [addAuditLog]);

  const bulkAction = useCallback(async (action: UserBulkAction, currentUser: PlatformUser) => {
    setLoading(true);
    try {
      setUsers(prev => prev.map(user => {
        if (!action.userIds.includes(user.id)) return user;

        let updatedUser = { ...user };
        switch (action.action) {
          case 'status':
            updatedUser.status = action.value;
            break;
          case 'role':
            updatedUser.role = action.value;
            break;
          case 'groups':
            updatedUser.groups = action.value;
            break;
          default:
            return user;
        }

        addAuditLog(user.id, 'BULK_UPDATE', `Bulk ${action.action} update`, currentUser.id);
        return updatedUser;
      }));

      if (action.action === 'delete') {
        setUsers(prev => prev.filter(user => !action.userIds.includes(user.id)));
        action.userIds.forEach(userId => {
          addAuditLog(userId, 'BULK_DELETE', 'User deleted in bulk action', currentUser.id);
        });
      }

      // In real implementation, this would be an API call
      // await api.users.bulkUpdate(action);
    } catch (error) {
      console.error('Failed to perform bulk action:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [addAuditLog]);

  const resetPassword = useCallback(async (userId: string, currentUser: PlatformUser) => {
    setLoading(true);
    try {
      setUsers(prev => prev.map(user => {
        if (user.id !== userId) return user;
        return {
          ...user,
          requirePasswordChange: true,
          profile: {
            ...user.profile,
            lastPasswordChange: new Date().toISOString()
          }
        };
      }));
      
      addAuditLog(userId, 'RESET_PASSWORD', 'Password reset initiated', currentUser.id);
      
      // In real implementation, this would be an API call
      // await api.users.resetPassword(userId);
    } catch (error) {
      console.error('Failed to reset password:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [addAuditLog]);

  const toggleTwoFactor = useCallback(async (userId: string, enabled: boolean, currentUser: PlatformUser) => {
    setLoading(true);
    try {
      setUsers(prev => prev.map(user => {
        if (user.id !== userId) return user;
        return {
          ...user,
          twoFactorEnabled: enabled
        };
      }));
      
      addAuditLog(
        userId, 
        'UPDATE_2FA', 
        `Two-factor authentication ${enabled ? 'enabled' : 'disabled'}`,
        currentUser.id
      );
      
      // In real implementation, this would be an API call
      // await api.users.updateTwoFactor(userId, enabled);
    } catch (error) {
      console.error('Failed to toggle two-factor auth:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [addAuditLog]);

  return {
    users: getFilteredUsers(),
    selectedUsers,
    filters,
    auditLogs,
    loading,
    setSelectedUsers,
    setFilters,
    addUser,
    updateUser,
    deleteUser,
    bulkAction,
    resetPassword,
    toggleTwoFactor
  };
}; 